import type { Module } from "vuex/types/index.d.ts";
import { $api } from "@/services/api";
import store from "@/store";
import type { RefiRatePlanViewModel, RefiRateViewModel } from "@/models/viewModels";

const refiLoanConfigurationModule: Module<any, any> = {
  namespaced: true,
  state: {
    refiRatePlans: [] as any[],
    refiLoanConfiguration: {
      applicationId: 0,
      selectedRatePlanId: undefined,
      selectedByApplicantId: undefined,
      repaymentTypeId: undefined,
    } as any,
    selectedRateTerm: 0 as any,
    selectedRateType: "" as string,
    selectedRatePlanId: 0 as any,
    selectedRatePlan: {} as any,
    selectedRateOption: {} as any,
    availableTerms: [] as number[],
    selectedRepaymentTypeId: 0 as number,
    gettingRatePlans: false as boolean,
    programPromoDisclaimerText: "" as string,
  },
  mutations: {
    SET_GETTING_RATE_PLANS(state: any, flag: boolean) {
      state.gettingRatePlans = flag;
    },
    SET_SELECTED_REPAYMENT_TYPE_ID(state: any, repaymentTypeId: number) {
      state.selectedRepaymentTypeId = repaymentTypeId;
    },
    SET_SELECTED_RATE_TYPE(state: any, rateType: string) {
      state.selectedRateType = rateType;
    },
    SET_AVAILABLE_TERMS(state: any, terms: any[]) {
      state.availableTerms = terms;
    },
    SET_REFI_RATE_PLANS(state: any, ratePlans: RefiRatePlanViewModel[]) {
      state.refiRatePlans = ratePlans;
    },
    SET_SELECTED_RATE_TERM(state: any, rateTerm: number) {
      state.selectedRateTerm = rateTerm;
    },
    SET_SELECTED_RATE_PLAN(state: any, selectedRatePlan: RefiRatePlanViewModel) {
      state.selectedRatePlan = selectedRatePlan;
    },
    SET_SELECTED_RATE_PLAN_ID(state: any, selectedRatePlanId: number) {
      state.selectedRatePlanId = selectedRatePlanId;
    },
    SET_SELECTED_RATE_OPTION(state: any, selectedRateOption: RefiRateViewModel) {
      state.selectedRateOption = selectedRateOption;
    },
    SET_REFI_LOAN_CONFIGURATION(state: any, loanConfiguration: any) {
      state.refiLoanConfiguration = loanConfiguration;
    },
    SET_PROGRAM_PROMO_DISCLAIMER_TEXT(state: any, disclaimer?: string) {
      state.programPromoDisclaimerText = disclaimer;
    },
  },
  actions: {
    setSelectedRatePlan({ commit }, payload) {
      commit("SET_SELECTED_RATE_PLAN", payload);
    },
    setSelectedRateOption({ commit }, payload) {
      commit("SET_SELECTED_RATE_OPTION", payload);
    },
    setSelectedRatePlanId({ commit }, payload) {
      commit("SET_SELECTED_RATE_PLAN_ID", payload);
    },
    setSelectedRateTerm({ commit }, payload) {
      commit("SET_SELECTED_RATE_TERM", payload);
    },
    setRefiLoanConfiguration({ commit }, payload) {
      commit("SET_REFI_LOAN_CONFIGURATION", payload);
    },
    setSelectedRateType({ commit }, payload) {
      commit("SET_SELECTED_RATE_TYPE", payload);
    },
    async getRefiRatePlans({ commit }) {
      commit("SET_GETTING_RATE_PLANS", true);
      try {
        const refiRatePlansResponse = await $api.refi.getRefiRatePlans(store?.state?.appData?.application?.id);
        if (refiRatePlansResponse && refiRatePlansResponse?.ratePlans?.length > 0) {
          commit("SET_PROGRAM_PROMO_DISCLAIMER_TEXT", refiRatePlansResponse?.promoDisclaimerText);
          commit("SET_REFI_RATE_PLANS", refiRatePlansResponse?.ratePlans);
          commit("SET_SELECTED_REPAYMENT_TYPE_ID", refiRatePlansResponse?.repaymentTypeId);
        } else {
          commit("SET_REFI_RATE_PLANS", []);
        }
      } catch (error) {
        console.log("Error: getRefiRatePlans()", error);
      }

      commit("SET_GETTING_RATE_PLANS", false);
    },
    async getLoanConfiguration({ commit }) {
      try {
        const loanConfigurationResponse = await $api.applications.getAppLoanConfig(
          store?.state?.appData?.application?.id,
        );
        commit("SET_REFI_LOAN_CONFIGURATION", loanConfigurationResponse);
      } catch (error) {
        console.log("Error: getRefiRatePlans()", error);
      }
    },
    async updateLoanConfiguration({ state, commit }) {
      const appLoanConfigRequest = {
        applicationId: store.state.appData.application.id,
        selectedRatePlanId: state?.selectedRatePlanId,
        selectedByApplicantId: store?.state?.appData?.applicants[store?.state?.appData?.applicants?.length - 1]?.id,
        repaymentTypeId: state?.selectedRepaymentTypeId,
      };

      const response = await $api.applications.addOrUpdateAppLoanConfig(appLoanConfigRequest);
      if (response && response?.applicationId) {
        commit("SET_REFI_LOAN_CONFIGURATION", appLoanConfigRequest);
        return true;
      } else {
        return false;
      }
    },
  },
  getters: {
    SelectedLoanConfiguration: (state: any): any => {
      return {
        selectedLoanTerm: `${state.selectedRateTerm} Year Term`,
        selectedInterestRate: state?.selectedRateOption?.rate || "Not Yet Selected",
        selectedRateTypeName: state?.selectedRateType,
        estimatedMonthlyLoanPayment: state.selectedRateOption?.estimatedPayment,
      };
    },
    AvailableRatePlans: (state: any): any => {
      return state.refiRatePlans;
    },
    StyleOption: (state: any): number => {
      const fixedAndVariable = [];
      const singleRateTypeOffered = [];
      const availableRatePlans = state.refiRatePlans;
      availableRatePlans?.forEach(function (ratePlan: any) {
        // Single rate type offered
        if (
          (ratePlan.hasFixedRate && !ratePlan.hasVariableRate) ||
          (!ratePlan.hasFixedRate && ratePlan.hasVariableRate)
        ) {
          singleRateTypeOffered.push(ratePlan.year);
        }
        // check if each plan offers BOTH fixed and variable
        if (ratePlan.hasFixedRate && ratePlan.hasVariableRate) {
          fixedAndVariable.push(ratePlan.year);
        }
      });

      return fixedAndVariable?.length > 0 && singleRateTypeOffered?.length > 0 ? 1 : 2;
    },
    FormattedTerms: (state: any): string => {
      return state.availableTerms?.map((n: any, index: any) => {
        return index === state.availableTerms?.length - 1 ? `or ${n}-year` : `${n}- ,`;
      });
    },
  },
};

export default refiLoanConfigurationModule;
